import { getSupabaseBrowserClient } from '../lib/supabase'

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}
function objects(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(object) : []
}
function text(value: unknown): string { return typeof value === 'string' ? value : '' }
function nullableText(value: unknown): string | null { return typeof value === 'string' && value ? value : null }
function numberValue(value: unknown): number { return typeof value === 'number' ? value : Number(value) || 0 }
function booleanValue(value: unknown): boolean { return value === true || value === 'true' }

async function invoke(functionName: string, args: Record<string, unknown> = {}) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc(functionName, args)
  if (error) throw new Error(error.message)
  return data
}

export type RiskRating = {
  stage: string
  versionNo: number
  suggestedLikelihood: number
  suggestedConsequence: number
  suggestedBand: string
  confirmedLikelihood: number | null
  confirmedConsequence: number | null
  confirmedBand: string | null
  effectiveLikelihood: number
  effectiveConsequence: number
  effectiveBand: string
  appetitePosition: string
  explanation: string
  confirmed: boolean
  stale: boolean
}

function rating(value: unknown): RiskRating {
  const row = object(value)
  return {
    stage: text(row.stage),
    versionNo: numberValue(row.versionNo),
    suggestedLikelihood: numberValue(row.suggestedLikelihood),
    suggestedConsequence: numberValue(row.suggestedConsequence),
    suggestedBand: text(row.suggestedBand),
    confirmedLikelihood: row.confirmedLikelihood == null ? null : numberValue(row.confirmedLikelihood),
    confirmedConsequence: row.confirmedConsequence == null ? null : numberValue(row.confirmedConsequence),
    confirmedBand: nullableText(row.confirmedBand),
    effectiveLikelihood: numberValue(row.effectiveLikelihood),
    effectiveConsequence: numberValue(row.effectiveConsequence),
    effectiveBand: text(row.effectiveBand),
    appetitePosition: text(row.appetitePosition),
    explanation: text(row.explanation),
    confirmed: booleanValue(row.confirmed),
    stale: booleanValue(row.stale),
  }
}

export type RiskRegisterRow = {
  id: string
  riskReference: string
  title: string
  riskStatement: string
  categoryCode: string
  categoryLabel: string
  aiUseCaseId: string
  aiUseTitle: string
  businessArea: string
  productName: string
  supplierName: string
  inherentRating: RiskRating
  residualRating: RiskRating
  appetitePosition: string
  ownerName: string
  treatmentDecision: string
  treatmentStatus: string
  status: string
  visibilityClass: string
  nextReviewDate: string | null
  lastMaterialChange: string | null
  overdueReview: boolean
  overdueAction: boolean
  acceptance: Record<string, unknown>
  controlStatus: Record<string, unknown>
}

function registerRow(value: unknown): RiskRegisterRow {
  const row = object(value)
  return {
    id: text(row.id), riskReference: text(row.riskReference), title: text(row.title), riskStatement: text(row.riskStatement),
    categoryCode: text(row.categoryCode), categoryLabel: text(row.categoryLabel), aiUseCaseId: text(row.aiUseCaseId), aiUseTitle: text(row.aiUseTitle),
    businessArea: text(row.businessArea), productName: text(row.productName), supplierName: text(row.supplierName),
    inherentRating: rating(row.inherentRating), residualRating: rating(row.residualRating), appetitePosition: text(row.appetitePosition), ownerName: text(row.ownerName),
    treatmentDecision: text(row.treatmentDecision), treatmentStatus: text(row.treatmentStatus), status: text(row.status), visibilityClass: text(row.visibilityClass),
    nextReviewDate: nullableText(row.nextReviewDate), lastMaterialChange: nullableText(row.lastMaterialChange), overdueReview: booleanValue(row.overdueReview), overdueAction: booleanValue(row.overdueAction),
    acceptance: object(row.acceptance), controlStatus: object(row.controlStatus),
  }
}

export async function listAiRisks(organisationId: string, filters: Record<string, unknown> = {}): Promise<RiskRegisterRow[]> {
  return objects(await invoke('list_ai_risks', { p_organisation_id: organisationId, p_filters: filters })).map(registerRow)
}

export async function getAiRiskDetail(riskId: string): Promise<Record<string, unknown>> {
  return object(await invoke('get_ai_risk_detail', { p_risk_id: riskId }))
}

export async function getAiRiskMatrix(organisationId: string, stage: 'inherent' | 'residual' = 'residual'): Promise<Record<string, unknown>> {
  return object(await invoke('get_ai_risk_matrix', { p_organisation_id: organisationId, p_stage: stage }))
}

export async function getAiRiskReportingSummary(organisationId: string): Promise<Record<string, unknown>> {
  return object(await invoke('get_ai_risk_reporting_summary', { p_organisation_id: organisationId }))
}

export async function getAiUseRiskSummary(aiUseCaseId: string): Promise<Record<string, unknown>> {
  return object(await invoke('get_ai_use_risk_summary', { p_ai_use_case_id: aiUseCaseId }))
}

export async function reportAiRiskConcern(aiUseCaseId: string, concernText: string, answers: Record<string, unknown>) {
  return object(await invoke('report_ai_risk_concern', { p_ai_use_case_id: aiUseCaseId, p_concern_text: concernText, p_answers: answers }))
}

export async function confirmAiRiskRating(riskId: string, stage: 'inherent' | 'residual', likelihood: number, consequence: number, reason: string) {
  return object(await invoke('confirm_ai_risk_rating', { p_risk_id: riskId, p_stage: stage, p_likelihood: likelihood, p_consequence: consequence, p_reason: reason }))
}

export async function recalculateAiRisk(riskId: string) {
  return object(await invoke('recalculate_ai_risk', { p_risk_id: riskId }))
}

export async function updateAiRiskManagement(riskId: string, values: {
  ownerMembershipId?: string | null
  treatmentDecision?: string | null
  treatmentStatus?: string | null
  nextReviewDate?: string | null
  visibilityClass?: string | null
}) {
  return object(await invoke('update_ai_risk_management', {
    p_risk_id: riskId,
    p_owner_membership_id: values.ownerMembershipId ?? null,
    p_treatment_decision: values.treatmentDecision ?? null,
    p_treatment_status: values.treatmentStatus ?? null,
    p_next_review_date: values.nextReviewDate ?? null,
    p_visibility_class: values.visibilityClass ?? null,
  }))
}

export async function reviewAiRiskControlEffectiveness(linkId: string, effective: boolean, notes: string) {
  return object(await invoke('review_ai_risk_control_effectiveness', { p_risk_control_link_id: linkId, p_effective: effective, p_notes: notes || null }))
}

export async function addRecommendedAiRiskControl(riskId: string, controlCode: string) {
  return text(await invoke('add_recommended_ai_risk_control', { p_risk_id: riskId, p_control_code: controlCode }))
}

export async function createAiRiskTreatmentAction(riskId: string, title: string, description: string, ownerMembershipId: string | null, dueDate: string | null) {
  return text(await invoke('create_ai_risk_treatment_action', {
    p_risk_id: riskId, p_title: title, p_description: description || null, p_owner_membership_id: ownerMembershipId, p_due_date: dueDate,
  }))
}

export async function requestAiRiskAcceptance(riskId: string) {
  return object(await invoke('request_ai_risk_acceptance', { p_risk_id: riskId }))
}

export async function getAiRiskAcceptanceTask(requestId: string): Promise<Record<string, unknown>> {
  return object(await invoke('get_ai_risk_acceptance_task', { p_request_id: requestId }))
}

export async function decideAiRiskAcceptance(requestId: string, decision: string, rationale: string, conditions: string | null) {
  return object(await invoke('decide_ai_risk_acceptance', { p_request_id: requestId, p_decision: decision, p_rationale: rationale, p_conditions: conditions }))
}
